import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useVerseUrl from "../../utils/useVerseUrl";
import { getRecentExplorers } from "../../utils/recentExplorers";
import { buildSearchResults } from "../../utils/searchResults";
import { useSearchCorpus } from "../../utils/searchCorpus";
import { useDocumentTitle } from "../../utils/pageTitle";
import "./pageNotFound.css";

const ROUTE_GROUPS = [
  {
    title: "Start Here",
    sectionClass: "nf-section--start",
    links: [
      { label: "Home", path: "/" },
      { label: "Women of the Bible", path: "/women-of-the-bible" },
      { label: "Enemies of the Bible", path: "/enemies-of-the-bible" },
      { label: "Nations of the Bible", path: "/nations-of-the-bible" },
    ],
  },
  {
    title: "Old Testament",
    sectionClass: "nf-section--ot",
    links: [
      { label: "Genesis", path: "/genesis" },
      { label: "Exodus", path: "/exodus" },
      { label: "Leviticus", path: "/leviticus" },
      { label: "Numbers", path: "/numbers" },
      { label: "Journey Through the Wilderness", path: "/exodus-deuteronomy" },
      { label: "Joshua", path: "/joshua" },
      { label: "Judges", path: "/judges" },
      { label: "Divided Kingdoms", path: "/1samuel-2chron" },
      { label: "Post-Exile", path: "/ezra-nehemiah-esther" },
      { label: "Job", path: "/job" },
      { label: "Psalms", path: "/psalms" },
      { label: "Proverbs", path: "/proverbs" },
      { label: "Ecclesiastes", path: "/ecclesiastes" },
      { label: "Song of Songs", path: "/song-of-songs" },
      { label: "Isaiah", path: "/isaiah" },
      { label: "Daniel", path: "/daniel" },
      { label: "Minor Prophets", path: "/minor-prophets" }
    ],
  },
    {
    title: "New Testament",
    sectionClass: "nf-section--nt",
    links: [
      { label: "A Walk Through the Gospels", path: "/walk-through-the-gospels" },
      { label: 'Acts & the Early Church', path: '/church-history'},  
      { label: 'Church Letters', path: '/church-letters' },
      { label: 'Revelation', path: '/revelation' }
    ],
  },
  {
    title: "Timelines",
    sectionClass: "nf-section--timeline",
    links: [
      { label: "Who Ruled the Biblical Land?", path: "/who-ruled-the-biblical-land" },
      { label: "David → Alexander", path: "/david-alexander" },
      { label: "Between the Testaments", path: "/between-the-testaments" },
      { label: "Alexander → Early Church", path: "/alexander-earlychurch" },
      { label: "Early Church History", path: "/early-church-history" },
      { label: "Early Church Fathers", path: "/early-church-fathers" },
    ],
  },
  {
    title: "Genealogy",
    sectionClass: "nf-section--genealogy",
    links: [
      { label: "Adam → Noah", path: "/adam-noah" },
      { label: "Japheth", path: "/japheth-lineage" },
      { label: "Ham", path: "/ham-lineage" },
      { label: "Shem", path: "/shem-lineage" },
      { label: "Abraham", path: "/abraham-lineage" },
      { label: "Haran", path: "/haran-lineage" },
      { label: "Nahor", path: "/nahor-lineage" },
      { label: "Jacob", path: "/jacob-lineage" },
      { label: "Esau", path: "/esau-lineage" },
      { label: "Saul", path: "/saul-lineage" },
      { label: "David", path: "/david-lineage" },
      { label: "Kings of Judah", path: "/kings-of-judah" },
      { label: "Moses & Aaron", path: "/moses-aaron" },
      { label: "Levi Line", path: "/levi-line" },
    ],
  },
];

function normalizePath(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9/ -]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function scoreLink(pathname, candidatePath, candidateLabel) {
  const source = normalizePath(pathname.replace(/^\//, ""));
  const targetPath = normalizePath(candidatePath.replace(/^\//, ""));
  const targetLabel = normalizePath(candidateLabel);

  if (!source) return 0;
  if (source === targetPath) return 100;
  if (targetPath.includes(source)) return 80;
  if (source.includes(targetPath)) return 70;
  if (targetLabel.includes(source)) return 60;

  const sourceParts = source.split(/[ /-]+/).filter(Boolean);
  const targetParts = `${targetPath} ${targetLabel}`
    .split(/[ /-]+/)
    .filter(Boolean);

  let matches = 0;

  sourceParts.forEach((part) => {
    if (targetParts.some((target) => target.includes(part) || part.includes(target))) {
      matches += 1;
    }
  });

  return matches * 10;
}

function dedupeByPath(items) {
  const seen = new Set();

  return items.filter((item) => {
    if (!item?.path || seen.has(item.path)) return false;
    seen.add(item.path);
    return true;
  });
}

function dedupeSuggestions(items) {
  const seen = new Set();

  return items.filter((item) => {
    const key = `${item.type}:${item.to || item.href || item.title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function SearchCard({ item }) {
  if (!item) return null;

  if (item.to) {
    return (
      <Link to={item.to} className="nf-card">
        <div className="nf-cardTitle">{item.title}</div>
        {item.subtitle ? <div className="nf-cardMeta">{item.subtitle}</div> : null}
        {item.meta ? <div className="nf-cardPath">{item.meta}</div> : null}
      </Link>
    );
  }

  if (item.href) {
    return (
      <a
        href={item.href}
        className="nf-card"
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="nf-cardTitle">{item.title}</div>
        {item.subtitle ? <div className="nf-cardMeta">{item.subtitle}</div> : null}
        {item.meta ? <div className="nf-cardPath">{item.meta}</div> : null}
      </a>
    );
  }

  return null;
}

function openSuggestion(item, navigate, fallbackQuery = "") {
  if (!item) {
    const trimmed = String(fallbackQuery || "").trim();
    if (trimmed) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    }
    return;
  }

  if (item.to) {
    navigate(item.to);
    return;
  }

  if (item.href) {
    window.open(item.href, "_blank", "noopener,noreferrer");
    return;
  }

  const trimmed = String(fallbackQuery || "").trim();
  if (trimmed) {
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }
}

/*
 * Shared not-found experience: recently visited pages, closest route matches
 * and a search box. Person and battle pages render this too, rather than each
 * carrying its own bare "not found" card.
 */
export default function NotFoundPage({
  title = "Page Not Found",
  message = "We could not find",
}) {
  useDocumentTitle(title);

  const verseUrl = useVerseUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);

  const attemptedValue = useMemo(() => {
    return decodeURIComponent(location.pathname.replace(/^\/+/, "") || "");
  }, [location.pathname]);

  const [searchQuery, setSearchQuery] = useState(attemptedValue);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setSearchQuery(attemptedValue);
  }, [attemptedValue]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!searchWrapRef.current?.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const bestRouteMatch = useMemo(() => {
    const allLinks = ROUTE_GROUPS.flatMap((group) =>
      group.links.map((link) => ({
        ...link,
        score: scoreLink(location.pathname, link.path, link.label),
      }))
    );

    const best = allLinks
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)[0];

    return best || null;
  }, [location.pathname]);

  const recentLinks = useMemo(() => {
    const recent = getRecentExplorers?.() || [];

    return dedupeByPath(
      recent
        .filter((item) => item?.path && item.path !== location.pathname)
        .map((item) => ({
          label: item.title || item.path,
          path: item.path,
          meta: item.section || "Recently Visited",
        }))
    ).slice(0, 4); 
  }, [location.pathname]);

  const suggestedLinks = useMemo(() => {
    const allLinks = ROUTE_GROUPS.flatMap((group) =>
      group.links.map((link) => ({
        ...link,
        meta: group.title,
        score: scoreLink(location.pathname, link.path, link.label),
      }))
    );

    return dedupeByPath(
      allLinks
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 4)
    );
  }, [location.pathname]);

  /*
   * Helping the reader find what they meant is this page's whole job, so the
   * corpus is fetched the moment it mounts rather than on interaction. It is
   * fetched, though, not imported: this component stays eager because the
   * person and battle pages render it inline, and importing the corpus here
   * put it in the main bundle for every visitor on every route.
   */
  const { corpus, prime: primeSearchCorpus } = useSearchCorpus();

  useEffect(() => {
    primeSearchCorpus();
  }, [primeSearchCorpus]);

  const { bestMatch, moreResults, hasResults } = useMemo(() => {
    return buildSearchResults(
      searchQuery,
      null,
      verseUrl,
      corpus?.getPeopleSuggestions
    );
  }, [searchQuery, verseUrl, corpus]);

  const hasMoreResults =
    (moreResults?.scripture?.length || 0) > 0 ||
    (moreResults?.people?.length || 0) > 0 ||
    (moreResults?.pages?.length || 0) > 0;

  const flatSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const suggestions = [];

    if (bestMatch) {
      suggestions.push({
        type: bestMatch.href
          ? "scripture"
          : bestMatch.to?.startsWith("/people/")
            ? "person"
            : "page",
        title: bestMatch.title,
        subtitle: bestMatch.subtitle || "",
        meta: bestMatch.meta || "Best Match",
        to: bestMatch.to || null,
        href: bestMatch.href || null,
      });
    }

    (moreResults?.scripture || []).slice(0, 4).forEach((reference) => {
      suggestions.push({
        type: "scripture",
        title: reference,
        subtitle: "Scripture",
        meta: "Open verse lookup",
        href: verseUrl(reference),
      });
    });

    (moreResults?.people || []).slice(0, 5).forEach((item) => {
      suggestions.push({
        type: "person",
        title: item.title,
        subtitle: item.subtitle || "Person",
        meta: item.route,
        to: item.route,
      });
    });

    (moreResults?.pages || []).slice(0, 5).forEach((item) => {
      suggestions.push({
        type: "page",
        title: item.title,
        subtitle: item.subtitle || "Page",
        meta: item.route,
        to: item.route,
      });
    });

    return dedupeSuggestions(suggestions).slice(0, 8);
  }, [bestMatch, moreResults, searchQuery, verseUrl]);

  useEffect(() => {
    setActiveSuggestionIndex(flatSuggestions.length > 0 ? 0 : -1);
  }, [flatSuggestions]);

  function handleSubmit(e) {
    e.preventDefault();

    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    const highlightedSuggestion =
      showSuggestions &&
      activeSuggestionIndex >= 0 &&
      flatSuggestions[activeSuggestionIndex]
        ? flatSuggestions[activeSuggestionIndex]
        : null;

    if (highlightedSuggestion) {
      openSuggestion(highlightedSuggestion, navigate, trimmed);
      setShowSuggestions(false);
      return;
    }

    if (bestMatch) {
      openSuggestion(bestMatch, navigate, trimmed);
      setShowSuggestions(false);
      return;
    }

    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setShowSuggestions(false);
  }

  function handleKeyDown(e) {
    if (!flatSuggestions.length) {
      if (e.key === "Enter") {
        handleSubmit(e);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setShowSuggestions(true);
      setActiveSuggestionIndex((prev) =>
        prev < flatSuggestions.length - 1 ? prev + 1 : 0
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setShowSuggestions(true);
      setActiveSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : flatSuggestions.length - 1
      );
      return;
    }

    if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  }

  function handleSuggestionMouseDown(item) {
    openSuggestion(item, navigate, searchQuery.trim());
    setShowSuggestions(false);
  }

  function clearSearch() {
    setSearchQuery("");
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    inputRef.current?.focus();
  }

  return (
    <div className="nf-page">
      <div className="nf-shell">
        <header className="pob-surface nf-hero">
          <div className="bookmark">Navigation Help • {title}</div>

          <h1 className="nf-title">{title}</h1>

          {bestRouteMatch && (
            <div className="nf-didYouMean">
              Did you mean{" "}
              <Link to={bestRouteMatch.path} className="nf-didYouMeanLink">
                {bestRouteMatch.label}
              </Link>
              ?
            </div>
          )}

          <p className="nf-subtitle">
            {message}{" "}
            <span className="nf-path">{location.pathname}</span>. Try one of the
            routes below to continue exploring.
          </p>
        </header>

        <section className="pob-surface nf-section nf-section--search">
          <div className="nf-sectionLabel">Try a Search Instead</div>

          <form className="nf-searchWrap" onSubmit={handleSubmit} ref={searchWrapRef}>
            <div className="nf-searchShell">
              <input
                ref={inputRef}
                className="nf-searchInput"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => {
                  if (flatSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search Scripture, people, books, events..."
                autoComplete="off"
              />

              {searchQuery ? (
                <button
                  type="button"
                  className="nf-searchClear"
                  aria-label="Clear search"
                  onClick={clearSearch}
                >
                  ×
                </button>
              ) : null}
            </div>

            {searchQuery.trim() && showSuggestions && flatSuggestions.length > 0 ? (
              <div className="nf-searchDropdown">
                {flatSuggestions.map((item, index) => (
                  <button
                    key={`${item.type}-${item.to || item.href || item.title}-${index}`}
                    type="button"
                    className={`nf-searchSuggestion ${
                      index === activeSuggestionIndex ? "nf-searchSuggestion--active" : ""
                    }`}
                    onMouseDown={() => handleSuggestionMouseDown(item)}
                    onMouseEnter={() => setActiveSuggestionIndex(index)}
                  >
                    <div className="nf-searchSuggestionTop">
                      <span className="nf-searchSuggestionTitle">{item.title}</span>
                      <span
                        className={`nf-searchSuggestionType nf-searchSuggestionType--${item.type}`}
                      >
                        {item.type === "person"
                          ? "Person"
                          : item.type === "page"
                            ? "Page"
                            : "Scripture"}
                      </span>
                    </div>

                    {item.subtitle ? (
                      <div className="nf-searchSuggestionMeta">{item.subtitle}</div>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </form>

          {searchQuery.trim() && !hasResults ? (
            <div className="nf-searchEmpty">
              No direct matches found for <strong>{searchQuery}</strong>.
            </div>
          ) : null}

          {bestMatch ? (
            <div className="nf-searchResults">
              <div className="nf-searchResultLabel">Best Match</div>
              <SearchCard item={bestMatch} />
            </div>
          ) : null}

          {searchQuery.trim() && hasMoreResults ? (
            <div className="nf-searchMore">
              {moreResults.scripture?.length > 0 ? (
                <div className="nf-searchGroup">
                  <div className="nf-searchGroupLabel">Scripture</div>
                  <div className="nf-grid">
                    {moreResults.scripture.slice(0, 4).map((reference) => (
                      <a
                        key={reference}
                        href={verseUrl(reference)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="nf-card"
                      >
                        <div className="nf-cardTitle">{reference}</div>
                        <div className="nf-cardMeta">Scripture</div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              {moreResults.people?.length > 0 ? (
                <div className="nf-searchGroup">
                  <div className="nf-searchGroupLabel">People</div>
                  <div className="nf-grid">
                    {moreResults.people.slice(0, 4).map((item) => (
                      <Link key={item.route} to={item.route} className="nf-card">
                        <div className="nf-cardTitle">{item.title}</div>
                        {item.subtitle ? (
                          <div className="nf-cardMeta">{item.subtitle}</div>
                        ) : null}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              {moreResults.pages?.length > 0 ? (
                <div className="nf-searchGroup">
                  <div className="nf-searchGroupLabel">Pages</div>
                  <div className="nf-grid">
                    {moreResults.pages.slice(0, 4).map((item) => (
                      <Link key={item.route} to={item.route} className="nf-card">
                        <div className="nf-cardTitle">{item.title}</div>
                        {item.subtitle ? (
                          <div className="nf-cardMeta">{item.subtitle}</div>
                        ) : null}
                        <div className="nf-cardPath">{item.route}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        {recentLinks.length > 0 ? (
          <section className="pob-surface nf-section nf-section--recent">
            <div className="nf-sectionLabel">Recently Visited</div>
            <div className="nf-grid">
              {recentLinks.map((item) => (
                <Link key={item.path} to={item.path} className="nf-card">
                  <div className="nf-cardTitle">{item.label}</div>
                  <div className="nf-cardMeta">{item.meta}</div>
                  <div className="nf-cardPath">{item.path}</div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {suggestedLinks.length > 0 ? (
          <section className="pob-surface nf-section nf-section--closest">
            <div className="nf-sectionLabel">Closest Matches</div>
            <div className="nf-grid">
              {suggestedLinks.map((item) => (
                <Link key={item.path} to={item.path} className="nf-card">
                  <div className="nf-cardTitle">{item.label}</div>
                  <div className="nf-cardMeta">{item.meta}</div>
                  <div className="nf-cardPath">{item.path}</div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {ROUTE_GROUPS.map((group) => (
          <section
            key={group.title}
            className={`pob-surface nf-section ${group.sectionClass || ""}`}
          >
            <div className="nf-sectionLabel">{group.title}</div>
            <div className="nf-grid">
              {group.links.map((link) => (
                <Link key={link.path} to={link.path} className="nf-card">
                  <div className="nf-cardTitle">{link.label}</div>
                  <div className="nf-cardPath">{link.path}</div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}